import React, { useCallback } from "react";
import { Modal, BlockStack, Text, List, Button, Banner, InlineStack } from "@shopify/polaris";
import { ExternalIcon } from "@shopify/polaris-icons";
import { useEnsureInstall } from "../../hooks/useEnsureInstall";
import { openRemoteUrl } from "../../utils/openRemoteUrl";
import { getShopifyAdminShippingSettingsUrl } from "../../utils/shippingSettingsLinks";
import { symbolFor } from "./BarWidget";

export default function EnableFreeShippingHelpModal({ open, onClose, goalAmount = 50, currency = "USD" }) {
  const { shop } = useEnsureInstall();
  const currencySymbol = symbolFor(currency);
  const formattedGoal =
    goalAmount != null && isFinite(Number(goalAmount))
      ? `${currencySymbol}${Number(goalAmount).toFixed(2)}`
      : `${currencySymbol}50.00`;

  const openShippingSettings = useCallback(() => {
    const url = getShopifyAdminShippingSettingsUrl(shop);
    openRemoteUrl(url, { newContext: true });
  }, [shop]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="How to enable free shipping in Shopify"
      primaryAction={{ content: "Close", onAction: onClose }}
      secondaryActions={[
        {
          content: "Open shipping settings",
          icon: ExternalIcon,
          onAction: openShippingSettings,
        },
      ]}
      large
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Banner tone="info">
            <p>
              This app shows a free-shipping progress bar on your storefront. It does{" "}
              <strong>not</strong> create shipping discounts or change checkout rates automatically.
              You must add a matching free-shipping rate in Shopify admin.
            </p>
          </Banner>

          <Text as="p" variant="bodyMd">
            Set the same order minimum as your bar goal ({formattedGoal}). If you use Shopify
            Markets, repeat these steps for each market or shipping zone.
          </Text>

          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Step 1 — Open shipping settings
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              In Shopify admin, go to <strong>Settings</strong> → <strong>Shipping and delivery</strong>.
            </Text>
            <InlineStack>
              <Button icon={ExternalIcon} onClick={openShippingSettings}>
                Open shipping settings
              </Button>
            </InlineStack>
          </BlockStack>

          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Step 2 — Add a free-shipping rate
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Under your shipping zone, click <strong>Add rate</strong>. Choose a price-based rate
              (or equivalent) and set the minimum order value to <strong>{formattedGoal}</strong> with
              a shipping price of <strong>{currencySymbol}0.00</strong>.
            </Text>
          </BlockStack>

          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Step 3 — Save and test checkout
            </Text>
            <List type="number">
              <List.Item>Save your shipping settings in Shopify admin.</List.Item>
              <List.Item>
                Add products to your cart until the cart total reaches {formattedGoal} or more.
              </List.Item>
              <List.Item>
                Proceed to checkout and confirm free shipping appears (or a {currencySymbol}0.00 rate).
              </List.Item>
            </List>
          </BlockStack>

          <Text as="p" variant="bodySm" tone="subdued">
            The progress bar on your storefront is for motivation only. Checkout honors the rates
            configured in Shopify Settings → Shipping and delivery.
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
